import EditIcon from "@/assets/colorable/edit.svg?react";
import PlusIcon from "@/assets/colorable/plus.svg?react";
import TimesIcon from "@/assets/colorable/times.svg?react";
import UploadIcon from "@/assets/colorable/upload.svg?react";
import PlusGradientIcon from "@/assets/light_gradient/plus.svg?react";
import Button from "@/components/Common/Button";
import SkeletonLoader from "@/components/onboarding/SkeletonLoader";
import clsxm from "@/lib/clsxm";
import {useImperativeHandle, type FC, type RefObject} from "react";
import {useDropzone, type Accept, type DropzoneRef} from "react-dropzone";
import {useTranslation} from "react-i18next";

const FileUpload: FC<{
  buttonTitle?: string;
  file?: File | string | null;
  isLoading?: boolean;
  onUpload(acceptedFiles: File[]): void;
  multiple?: boolean;
  accept?: Accept;
  variant?: "CIRCULAR" | "PLUS" | "RECTANGULAR";
  disabled?: boolean;
  fileUploadRef?: RefObject<DropzoneRef>;
  className?: string;
  iconClassName?: string;
}> = ({
  buttonTitle,
  file,
  isLoading,
  onUpload,
  multiple = false,
  accept = {
    "image/*": [],
  },
  variant = "CIRCULAR",
  disabled = false,
  fileUploadRef,
  className,
  iconClassName,
}) => {
  const {t} = useTranslation("common");
  const fileToShow = file instanceof File ? URL.createObjectURL(file) : file;

  const {
    getInputProps,
    getRootProps,
    isDragActive,
    isDragReject,
    open: openFileDialog,
  } = useDropzone({
    onDrop: onUpload,
    multiple,
    accept,
    noKeyboard: true,
    disabled,
  });

  useImperativeHandle(fileUploadRef, () => ({open: openFileDialog}));

  if (variant === "PLUS") {
    return (
      <div {...getRootProps()} className={disabled ? "cursor-not-allowed" : "cursor-pointer"}>
        <input {...getInputProps()} />
        <button
          className={clsxm(
            "border-primary-light-solid flex size-9 items-center justify-center rounded-sm border",
            disabled && "cursor-not-allowed",
          )}
          disabled={disabled}
          onClick={() => null}>
          <PlusGradientIcon />
        </button>
      </div>
    );
  }

  if (variant === "RECTANGULAR") {
    return (
      <div {...getRootProps()} className={disabled ? "cursor-not-allowed" : "cursor-pointer"}>
        <input {...getInputProps()} />
        <div
          className={clsxm(
            "border-neutral-grey-light hover:bg-neutral-white-dark/30 flex flex-col items-center justify-between gap-4 rounded-md border border-dashed p-5 transition-all duration-150 sm:flex-row",
            isDragReject && "border-danger-medium-red",
            isDragActive && !isDragReject && "border-primary-dark-solid",
            className,
          )}>
          <span className="text-neutral-grey-light flex-1 text-center text-sm">{t("or_drop_file_here")}</span>
          <Button
            onClick={() => null}
            shouldStopPropagation={false}
            buttonStyle="Blue"
            className="h-fit gap-2.5 px-8 py-3.5">
            <UploadIcon className="h-3 shrink-0" />
            {buttonTitle ?? t("select_file")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div {...getRootProps()} className={disabled ? "cursor-not-allowed" : "cursor-pointer"}>
      <input {...getInputProps()} />
      {!fileToShow ? (
        isLoading ? (
          <SkeletonLoader className={clsxm("size-44", className)} />
        ) : (
          <div
            className={clsxm(
              "group border-primary-dark-solid hover:border-primary-midnight size-44 rounded-full border-2 border-dashed bg-transparent object-cover transition-all duration-150",
              isDragReject && "border-danger-medium-red",
              isDragActive && !isDragReject && "border-primary-midnight border-2",
              className,
            )}>
            <div className="flex h-full w-full items-center justify-center">
              {isDragReject ? (
                <TimesIcon
                  className={clsxm(
                    "border-primary-light-solid bg-primary-dark-solid group-hover:bg-primary-midnight text-danger-raspberry flex size-12 items-center justify-center rounded-full border p-3.5 shadow-md transition-all duration-150",
                    isDragActive && !isDragReject && "bg-primary-midnight",
                    isDragReject && "border-danger-medium-red bg-danger-light-red",
                    iconClassName,
                  )}
                />
              ) : (
                <PlusIcon
                  className={clsxm(
                    "border-primary-light-solid bg-primary-dark-solid group-hover:bg-primary-midnight flex size-12 items-center justify-center rounded-full border p-3.5 text-white shadow-md transition-all duration-150",
                    isDragActive && !isDragReject && "bg-primary-midnight",
                    isDragReject && "border-danger-medium-red bg-danger-light-red",
                    iconClassName,
                  )}
                />
              )}
            </div>
          </div>
        )
      ) : (
        <div
          className={clsxm(
            "group border-primary-light-solid hover:border-primary-midnight relative size-44 rounded-full border-2 shadow-md transition-all duration-150",
            isDragReject && "border-danger-medium-red",
            isDragActive && !isDragReject && "border-primary-midnight border-2",
            className,
          )}>
          <img src={fileToShow} alt="" className="h-full w-full rounded-full object-cover" />
          {!disabled && (
            <div
              className={clsxm(
                "border-primary-light-solid group-hover:border-primary-midnight absolute top-0 right-0 flex size-12 items-center justify-center rounded-full border bg-white p-3 shadow-lg transition-all duration-150",
                isDragActive && !isDragReject && "border-primary-midnight bg-primary-midnight",
                isDragReject && "border-danger-medium-red bg-danger-light-red",
                iconClassName,
              )}>
              {isDragReject ? (
                <TimesIcon className="size-4 shrink-0" />
              ) : (
                <EditIcon
                  className={
                    "size-4.5 transition-all duration-150 " +
                    (isDragActive && !isDragReject ? "text-white" : "text-primary-dark-solid")
                  }
                />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FileUpload;
